import mapboxgl from 'mapbox-gl'
import { env } from '@/api/tools'
import { store } from '@/store'
import axios from 'axios'
import { getLiveProgress } from '@/api/tripply'
import { RouteGeometry, Place, RouteLiveState, RouteGeometryLegs, Location, LiveData, LocationModel, CoordinatesModel, Coordinates } from '@/api/tripply/models'
import { createTooltip, hideTooltip } from './tooltip.service'

export interface MapMarkers {
  [key: string]: mapboxgl.Marker;
}



export const searchLocations = async (query: string, limit: number): Promise<Location[]> => {
  const URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURI(query) + '.json'
  
  if (query.length > 2) {
    const response = await axios.get(URL, {
      params: {
        'access_token': mapboxgl.accessToken,
        'limit': limit || 5,
        'country': 'dk'
      },
      headers: {
        'Access-Control-Allow-Origin': '*'
      }
    })

    const features: any[] = response.data.features

    const locations = features.map(feature => {
      const coords = new CoordinatesModel(feature.center[1], feature.center[0])
      return new LocationModel(feature.place_name, coords, feature.place_type[0], feature.place_name.split(',')[0] || feature.text)
    })

    return locations
  }
  return []
}

export const searchCoordinates = async (coords: Coordinates) => {
  const locations = await searchLocations(coords.lon + ',' + coords.lat, 1)
  return locations[0] || new LocationModel('Unknown', coords, 'poi')
}



// Create estimate marker
const el = document.createElement('div')
el.className = 'estimate'
el.style.width = '24px'
el.style.height = '24px'
const estimateMarker: mapboxgl.Marker = new mapboxgl.Marker(el, {offset: [0, 0]})


const styles: { [theme: string]: string } = {
  light: env('MAPBOX_STYLE_DEFAULT') || env('MAPBOX_STYLE_LIGHT'),
  dark: env('MAPBOX_STYLE_DARK'),
  default: env('MAPBOX_STYLE_DEFAULT')
}

mapboxgl.accessToken = env('MAPBOX_ACCESS_TOKEN')

const createGeoJSONSource = (coordinates: [number, number][] ): mapboxgl.GeoJSONSourceRaw => {
  return {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coordinates},
      properties: {}
    }
  }
}

export const setTheme = async (map: mapboxgl.Map, theme?: string): Promise<mapboxgl.Map> => {
  return new Promise((resolve) => {
    const style = (styles[theme || store.global.state.theme] || styles['default'])
    if (style != map.getStyle()?.sprite?.replace('sprites','styles')) {
      map.setStyle(style)
      map.on('style.load', () => {
        resolve(map)
      })
    } else {
      resolve(map)
    }
  })
}

export const createMap = async (containerId: string): Promise<mapboxgl.Map> => {
  console.count('MAP INITIALIZED')
  
  const map = new mapboxgl.Map({
    container: containerId,
    center: [11, 56],
    zoom: 6.5,
    maxZoom: 18
  })
  map.on('drag', () => hideTooltip())
  return setTheme(map)
}

export const fitBounds = (map: mapboxgl.Map, coordinates: [number,number][]) => {
  if (!coordinates.length) return
  const bounds = coordinates.reduce((bounds, coord) => {
    return bounds.extend(coord)
  }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]))
  map.fitBounds(bounds, { padding: 100 })
}

export const drawPlaceMarker = (map: mapboxgl.Map, place: Place, placeIndex: number): mapboxgl.Marker => {
  const el = document.createElement('div')
  el.className = 'marker'
  el.style.width = '25px'
  el.style.height = '40px'

  createTooltip(el, place.name)

  if (place.first || place.last) {
    el.classList.add('firstlast')
  }
  el.setAttribute('data-index', place.index.toString())
  el.setAttribute('data-name', place.name)
  el.setAttribute('data-symbol', place.first ? 'A' : place.last ? 'B' : (placeIndex + 1).toString())

  return new mapboxgl.Marker(el, {offset: [0, 0]})
    .setLngLat([place.coords.lon, place.coords.lat])
    .addTo(map)
}

export const drawPlaceMarkers = (map: mapboxgl.Map, places: Place[]): MapMarkers => {
  return places.reduce((markers, place, i) => {
    markers[place.id] = drawPlaceMarker(map, place, i)
    return markers
  }, {} as MapMarkers)
}

export const updateMarkers = (map: mapboxgl.Map, markers: MapMarkers, liveData?: LiveData) => {
  if (liveData) {
    for (const markerId in markers) {
      const marker = markers[markerId]
      const el = marker.getElement()
      const index = parseInt(el.getAttribute('data-index') || '-1')
      if (index > liveData.latestIndex) {
        el.classList.add('coming')
      } else {
        el.classList.remove('coming')
      }
      if (index === liveData.latestIndex) {
        el.classList.add('current')
      } else {
        el.classList.remove('current')
      }
    }
  }
}

export const createDriverMarker = (map: mapboxgl.Map, live: LiveData): mapboxgl.Marker | undefined => {
  if (live.driver && live.driver.position) {
    const el = document.createElement('div')
    el.className = 'driver'
    el.style.width = '24px'
    el.style.height = '24px'

    createTooltip(el, 'Estimeret lokation')

    return new mapboxgl.Marker(el, {offset: [0, 0]})
      .setLngLat(live.driver.position)
      .addTo(map)
  }
}
export const updateDriverMarker = (driverMarker: mapboxgl.Marker, live: LiveData) => {
  if (driverMarker && live.driver?.position) {
    driverMarker.setLngLat([live.driver.position.lon, live.driver.position.lat])
  }
}

export const removeMarker = (marker: mapboxgl.Marker) => {
  marker.remove()
}

export const removeMarkers = (markers: MapMarkers) => {
  for (const markerId in markers) {
    removeMarker(markers[markerId])
  }
}


const updateSource = (map: mapboxgl.Map, id: string, source: mapboxgl.GeoJSONSourceRaw) => {
  const mapSource = map.getSource(id) as mapboxgl.GeoJSONSource
  mapSource.setData(source.data as mapboxgl.MapboxGeoJSONFeature)
}

const flattenLegs = (legs: [number, number][][]): [number, number][] => {
  return [].concat(...legs as [])
}

export const addRoute = (map: mapboxgl.Map, geometry: RouteGeometry, animate = false) => {
  const coords: [number, number][] = flattenLegs(geometry.legs)
  const source = createGeoJSONSource(coords)
  
  // fitBounds(map, coords)
}

const createRouteLayers = (map: mapboxgl.Map) => {
  if (!map.getSource('routeLine')) {
    map.addSource('routeLine', createGeoJSONSource([]))
  }
  if (!map.getSource('routeLineHint')) {
    map.addSource('routeLineHint', createGeoJSONSource([]))
  }
  if (!map.getLayer('route')) {
    map.addLayer({
      'id': 'routeHint',
      'type': 'line',
      'source': 'routeLineHint',
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': '#0066ff',
        'line-width': 4,
        'line-opacity': 0.7,
        'line-blur': 2,
        'line-dasharray': [0.2,2]
      }
    })
    map.addLayer({
      'id': 'route',
      'type': 'line',
      'source': 'routeLine',
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': '#0066ff',
        'line-width': 4,
        'line-opacity': 1,
        'line-blur': 2
      }
    })
  }
}

let timer: NodeJS.Timeout
export const drawGeometry = (map: mapboxgl.Map, geometry: RouteGeometry, liveState?: RouteLiveState) => {
  createRouteLayers(map)
  clearInterval(timer)  
  
  const legs: RouteGeometryLegs = geometry.legs
  // Live
  if (liveState) {
    //let l = 0 // leg index
    const futureCoords = flattenLegs(legs.slice(liveState.latestIndex + 1))
    const drivenCoords = flattenLegs(legs.slice(0, liveState.latestIndex + 1))
    updateSource(map, 'routeLine', createGeoJSONSource(drivenCoords))
    updateSource(map, 'routeLineHint', createGeoJSONSource(futureCoords))

    // Animate progress if driving
    if (liveState.driving) {
      const nextLeg = legs[liveState.latestIndex + 1]
      // fitBounds(map, nextLeg)
      clearInterval(timer)
      timer = setInterval(async () => {        
        const { ratio } = await getLiveProgress(liveState)
        const progressIndex = Math.ceil(nextLeg.length * ratio)
        const progressCoords = nextLeg.slice(0,progressIndex)
        const totalCoords = [...drivenCoords, ...progressCoords]
        requestAnimationFrame(() => {
          estimateMarker.setLngLat(progressCoords[progressCoords.length - 1]).addTo(map)
          updateSource(map, 'routeLine', createGeoJSONSource(totalCoords))
        })
        if (ratio >= 1) {
          clearInterval(timer)
        }
      }, 1000)
      // map.jumpTo({ 'center': nextLeg[0], 'zoom': 14 })
    } else {
      const latestLocation = drivenCoords[drivenCoords.length - 1]         
      latestLocation && estimateMarker.setLngLat(latestLocation).addTo(map)
      /*map.flyTo({ center: liveState.driver?.position || latestLocation,
        zoom: 14 })*/
    }
  }
  // Not live
  else {    
    requestAnimationFrame(() => {
      estimateMarker.remove()
      const coords = [].concat(...legs as [])
      updateSource(map, 'routeLine', createGeoJSONSource(coords))
      updateSource(map, 'routeLineHint', createGeoJSONSource([]))
      fitBounds(map, coords)
    })
  }
}

export const setZoom = (map: mapboxgl.Map, zoom: number) => {
  if (zoom < map.getMaxZoom() && zoom > map.getMinZoom()) {
    //map.setZoom(zoom, {animate: true})
    map.flyTo({
      zoom: zoom
    })
  }
}

export const zoom = (map: mapboxgl.Map, change: number) => {
  setZoom(map, map.getZoom() + change)
}

export const flyTo = (map: mapboxgl.Map, location: Location, zoom?: number) => {
  map.flyTo({
    center: [location.coords.lon, location.coords.lat],
    zoom: zoom || 13,
    pitch: 0,
    bearing: 0
  })
}

export const setLanguage = (map: mapboxgl.Map, language: string) => {
  // TODO: SET LANGUAGE
}

